<template>
  <div v-if="shift && associate" class="shift-info-main">
    <button
      v-if="canOpenAssociateProfile"
      type="button"
      class="shift-info-main__photo-button"
      :aria-label="`Open ${associateName || 'associate'} profile`"
      @click="openProfile(associate)"
    >
      <user-avatar :width="112" :height="112" :image-path="associate.photo_url" :has-modal="false" />
    </button>
    <div v-else class="shift-info-main__photo-frame">
      <user-avatar :width="112" :height="112" :image-path="associate.photo_url" />
    </div>

    <div class="shift-info-main__content">
      <button
        v-if="canOpenAssociateProfile"
        type="button"
        class="shift-info-main__name-button"
        @click="openProfile(associate)"
      >
        {{ associateName || '-' }}
      </button>
      <h3 v-else class="m-0">{{ associateName || '-' }}</h3>

      <div class="shift-info-main__identity-grid">
        <div class="shift-info-field">
          <span>Associate ID</span>
          <strong>{{ associate.associate_number || '-' }}</strong>
        </div>
        <div class="shift-info-field">
          <span>Role</span>
          <strong>{{ associate.role?.name || '-' }}</strong>
        </div>
        <div class="shift-info-field">
          <span>Employment</span>
          <strong :title="employment">{{ employment || '-' }}</strong>
        </div>
      </div>

      <div class="shift-info-main__facts-grid">
        <div class="shift-info-field">
          <span>Shift Date</span>
          <strong>{{ formattedDate }}</strong>
        </div>
        <div class="shift-info-field">
          <span>Worked Time</span>
          <strong>{{ workedTime }}</strong>
        </div>
        <div class="shift-info-field">
          <span>Duration</span>
          <strong>{{ workedDuration }}</strong>
        </div>
      </div>
    </div>

    <div class="shift-info-main__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import UserAvatar from '@/components/ui/user-avatar.vue'
import permissionMixin from '@/mixins/permissionMixin'
import { formatEmployment } from '@/modules/profile/utils/profile-formatters'
import util from '@/util'

const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'user-avatar': UserAvatar
  },
  mixins: [permissionMixin],
  props: {
    shift: {
      type: Object,
      default: undefined
    },
    associate: {
      type: Object,
      default: undefined
    }
  },
  computed: {
    associateName() {
      return `${util.titleCase(this.associate.first_name)} ${util.titleCase(this.associate.last_name)}`.trim()
    },

    employmentPaymentInfo() {
      return (
        this.associate.payment_info ?? {
          type: this.shift.internal_payment_info.payment_type,
          method: this.shift.internal_payment_info.pay_method,
          legal_entity: this.shift.internal_payment_info.legal_entity
        }
      )
    },

    employment() {
      return formatEmployment(this.employmentPaymentInfo)
    },

    canOpenAssociateProfile() {
      return Boolean(this.associate.associate_number && this.hasPermission('manage_associates'))
    },

    formattedDate() {
      const start = moment(this.shift.scheduled_start).utc(true)
      const end = moment(this.shift.scheduled_end).utc(true)

      let result = start.format(this.$store.state.applicationState.dateFormat)

      if (!start.isSame(end, 'day')) {
        result += ` - ${end.format(this.$store.state.applicationState.dateFormat)}`
      }

      return result
    },

    workedTimeRange() {
      const ranges = [
        [this.shift.work_start, this.shift.work_end],
        [this.shift.actual_work_start, this.shift.actual_work_end],
        [this.shift.scheduled_start, this.shift.scheduled_end]
      ]

      return ranges.find(([start, end]) => start && end) ?? [null, null]
    },

    workedTime() {
      const [start, end] = this.workedTimeRange
      if (!start || !end) return '-'

      const format = this.$store.state.applicationState.timeFormat
      return `${util.convertDateTimeToCurrentTimeZoneWithFormat(
        start,
        format
      )} - ${util.convertDateTimeToCurrentTimeZoneWithFormat(end, format)}`
    },

    workedDuration() {
      const [start, end] = this.workedTimeRange
      return start && end ? util.getFormattedDuration(start, end) : '-'
    }
  },
  methods: {
    ...modalActions(['openModal']),

    openProfile(record) {
      if (!record?.associate_number || !this.hasPermission('manage_associates')) return

      this.openModal({
        type: 'profile',
        data: {
          visible: true,
          associate_number: record.associate_number,
          edit: () => {},
          open_tab: 'personal'
        }
      })
    }
  }
}
</script>

<style scoped>
.shift-info-main {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr) 200px;
  gap: 16px;
  align-items: start;
}

.shift-info-main__photo-button,
.shift-info-main__name-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.shift-info-main__photo-button,
.shift-info-main__photo-frame {
  width: 112px;
  height: 112px;
  border-radius: var(--border-radius);
  overflow: hidden;
}

.shift-info-main__photo-button ::v-deep .user-avatar,
.shift-info-main__photo-frame ::v-deep .user-avatar,
.shift-info-main__photo-frame ::v-deep img {
  width: 100% !important;
  height: 100% !important;
}

.shift-info-main__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.shift-info-main__name-button {
  width: fit-content;
  max-width: 100%;
  color: var(--text-color);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  overflow-wrap: anywhere;
}

.shift-info-main__name-button:hover {
  color: var(--primary-color);
}

.shift-info-main__identity-grid,
.shift-info-main__facts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px 16px;
}

.shift-info-main__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shift-info-field {
  min-width: 0;
}

.shift-info-field > span {
  display: block;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.shift-info-field > strong {
  display: block;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

@media (max-width: 900px) {
  .shift-info-main {
    grid-template-columns: 80px minmax(0, 1fr);
  }

  .shift-info-main__photo-button,
  .shift-info-main__photo-frame {
    width: 80px;
    height: 80px;
  }

  .shift-info-main__actions {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 12px 0 0;
    border-top: 1px solid var(--border-color-base);
    border-left: 0;
  }
}

@media (max-width: 600px) {
  .shift-info-main__identity-grid,
  .shift-info-main__facts-grid {
    grid-template-columns: 1fr;
  }
}
</style>
